import { Component, Element, Event, EventEmitter, Method, Prop, State, VNode, Watch, h } from '@stencil/core';

import { height as iconSearchHeight, svgPathData as iconSearchPath, width as iconSearchWidth } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { height as iconTimesHeight, svgPathData as iconTimesPath, width as iconTimesWidth } from '@fortawesome/pro-light-svg-icons/faTimesCircle';

import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Dictionary } from '../core/classes/Dictionary';
import {
  InputChangeEvent,
  InputChangeHandler,
  InputClearEvent,
  InputClearHandler,
  SearchSubmitEvent,
  SearchSubmitHandler,
  SearchSuggestion,
  SuggestionClickEvent,
  SuggestionClickHandler,
} from './ads-search.types';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { generateSVGImage, isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/index';

/**
 * Search enables users to specify a word or a phrase to find particular relevant pieces of content without the use of navigation.
 * Search can be used as the primary means of discovering content, or as a filter to aid the user in finding content.
 * @tag `<ads-search>`
 * @example <ads-search has-search-button placeholder="Hello world"></ads-search>
 */
@Component({
  tag: 'ads-search',
  styleUrl: 'ads-search.scss',
  shadow: true,
})
export class AdsSearch implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  private _suggestionsLimit = 10;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-search'));
    this.component = 'ads-search';
    Dictionary.registerComponent(this);
  }

  @Element() private el: HTMLAdsSearchElement;

  /**
   * The search value the user types in is stored here
   * @hidden
   */
  @State() _searchValue: string;

  /**
   * The current active suggestion index
   * @hidden
   */
  @State() activeSuggestionIndex = 0;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * Decides whether or not the search should be autofocused (optional)
   * @default false
   */
  @Prop({ attribute: 'autofocus', reflect: true }) autoFocus = false;

  /**
   * The value for the aria-label of the search button, to describe the action (optional)
   * @default "Submit Search"
   */
  @Prop({ reflect: true }) buttonLabel = Dictionary.get('Submit Search', 'SUBMIT_SEARCH');

  /**
   * Sets whether or not the search should be is disabled (optional)
   * @default false
   */
  @Prop({ attribute: 'disabled', reflect: true }) disabled = false;

  /**
   * The value for the aria-label, to describe the purpose of the search input (optional)
   * @default "Search"
   */
  @Prop({ reflect: true }) inputLabel = Dictionary.get('Search', 'SEARCH');

  /**
   * The placeholder for the search input box (optional)
   * @default "Search"
   */
  @Prop({ attribute: 'placeholder', reflect: true }) placeholder = Dictionary.get('Search', 'SEARCH');

  /**
   * The maxlength property for search input (optional)
   */
  @Prop({ attribute: 'maxlength', reflect: true }) maxlength?: number;

  /**
   * The name property of the input field (optional)
   */
  @Prop({ attribute: 'name', reflect: true }) name?: string;

  /**
   * Whether or not empty results should be submitted (optional)
   * @default true
   */
  @Prop({ reflect: true }) submitEmptyResults = true;

  /**
   * The value for the search input box (optional)
   */
  @Prop({ reflect: true }) searchValue?: string;

  /**
   * Decides whether or not the search should be styled for developer purposes (optional)
   * @default false
   */
  @Prop({ reflect: true }) hasSearchButton = false;

  /**
   * Whether or not the clear button should be hidden (optional)
   * @default false
   */
  @Prop({ reflect: true }) hideClearButton? = false;

  /**
   * Data to show in suggestions (optional)
   */
  @Prop() suggestionsData?: SearchSuggestion[];

  /**
   * Handles inputClear events (optional)
   */
  @Prop() inputClearHandler?: InputClearHandler;

  /**
   * Handles inputChange events (optional)
   */
  @Prop() inputChangeHandler?: InputChangeHandler;

  /**
   * Handles submit events (optional)
   */
  @Prop() searchSubmitHandler?: SearchSubmitHandler;

  /**
   * Handles suggestion click events (optional)
   */
  @Prop() suggestionClickHandler?: SuggestionClickHandler;

  /**
   * Emits 'inputClear' custom event
   *
   * Usage: `document.querySelector('ads-search').addEventListener('inputClear', function(evt) { });`
   * @event
   */
  @Event({ bubbles: false }) inputClear: EventEmitter<InputClearEvent>;

  /**
   * Emits 'inputChange' custom event.<br/>
   * Usage: `document.querySelector('ads-search').addEventListener('inputChange', function(evt) { });`
   * @event
   */
  @Event({ bubbles: false }) inputChange: EventEmitter<InputChangeEvent>;

  /**
   * Emits the 'searchSubmit' custom event.<br/>
   * Usage: `document.querySelector('ads-search').addEventListener('searchSubmit', function(evt) { });`
   */
  @Event({ bubbles: false }) searchSubmit: EventEmitter<SearchSubmitEvent>;

  /**
   * Emits 'suggestionClick' custom event.<br/>
   * Usage: `document.querySelector('ads-search').addEventListener('suggestionClick', function(evt) { });`
   * @event
   */
  @Event({ bubbles: false }) suggestionClick: EventEmitter<SuggestionClickEvent>;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-search'));
  }

  /**
   * On searchValue prop change
   * Change search value if differes from old value
   * @param {string} newValue The new value
   * @param {string} oldValue The old value
   * @hidden
   */
  @Watch('searchValue')
  onSearchValueChange(newValue: string, oldValue: string): void {
    if (newValue !== oldValue) {
      this._searchValue = newValue;
    }
  }

  /**
   * Returns the current value of the search input
   * @return {Promise<string>}  The search input
   */
  @Method()
  value(): Promise<string> {
    return Promise.resolve(this._searchValue);
  }

  /* EVENT HANDLERS */

  /**
   * Clear search data
   * @param {Event} evt The event obj
   * @return {void}
   */
  private onInputClearHandler = (evt: Event): void => {
    evt.preventDefault();
    this._searchValue = '';
    this.getSearchBox().focus();
    this.inputChange.emit({ evt, value: this._searchValue });
    if (!isNullOrUndefined(this.inputChangeHandler)) {
      this.inputChangeHandler({ evt, value: this._searchValue });
    }
    this.inputClear.emit({ evt });
    if (!isNullOrUndefined(this.inputClearHandler)) {
      this.inputClearHandler({ evt });
    }
  };

  /**
   * Handle input
   * @param {Event} evt The evt obj
   * @return {void}
   */
  private onInputChangeHandler = (evt: Event): void => {
    this.resetHighlightedSuggestion();
    this._searchValue = (evt.target as HTMLInputElement).value;
    if (!isNullOrUndefined(this.inputChangeHandler)) {
      this.inputChangeHandler({ evt, value: this._searchValue });
    }
    this.inputChange.emit({ evt, value: this._searchValue });
  };

  /**
   * Handles suggestions display from searchbox onFocus event
   * @return {void}
   */
  private onFocusHandler = (): void => {
    const suggestionBox = this.getSuggestionsBox();
    if (suggestionBox && suggestionBox.classList.contains('is-hidden')) {
      suggestionBox.classList.remove('is-hidden');
    }
  };

  /**
   * Handles suggestions display from searchbox onBlur event
   * @param {Event} evt The event obj
   * @return {void}
   */
  private onBlurHandler = (evt: FocusEvent): void => {
    const target = evt.relatedTarget as HTMLElement;
    const suggestionBox = this.getSuggestionsBox();
    if (target && target.classList.contains('c-search__suggestions__suggestion')) {
      this.onSuggestionClickHandler(evt, this.suggestionsData[this.activeSuggestionIndex - 1]);
    }

    if (suggestionBox && !suggestionBox.classList.contains('is-hidden')) {
      if (this.activeSuggestionIndex) {
        suggestionBox.children[this.activeSuggestionIndex - 1].classList.remove('is-active');
      }
      suggestionBox.removeAttribute('aria-active-suggestion');
      suggestionBox.classList.add('is-hidden');
    }

    this.onInputClickHandler();

    if (!this.hasSearchButton) {
      if (typeof target === 'object' && target !== null) {
        if (this.el.shadowRoot.contains(target)) {
          return;
        }
      }
    }
  };

  /**
   * Handles up/down arrow key events to navigate through suggestions
   * @param {Event} evt The event obj
   * @return {void}
   */
  private onKeyUpHandler = (evt: KeyboardEvent): void => {
    if (this._searchValue && this.suggestionsData?.length > 0) {
      switch (evt.key.toLowerCase()) {
        case 'enter': // ENTER
          // Go to highlighted suggestion otherwise enter the text
          if (this.activeSuggestionIndex > 0) {
            evt.preventDefault();
            this.onSuggestionClickHandler(evt, this.suggestionsData[this.activeSuggestionIndex - 1]);
          }
          break;
        case 'escape': // ESCAPE
          evt.preventDefault();
          this.onInputClickHandler();
          this.onInputClearHandler(evt);
          break;
        case 'arrowup': // UP
          evt.preventDefault();
          this.setActiveSuggestion(this.activeSuggestionIndex - 1);
          break;
        case 'arrowdown': // DOWN
          evt.preventDefault();
          this.setActiveSuggestion(this.activeSuggestionIndex + 1);
          break;
        default:
          break;
      }
    }
  };

  /**
   * Reset the active highlighted suggestion
   * @return {void}
   */
  private onInputClickHandler = (): void => {
    this.resetHighlightedSuggestion();
  };

  /**
   * Handles form submission
   * @param  {Event}  evt The event obj
   * @return {void}
   */
  private onSubmitHandler = (evt: Event): void => {
    evt.preventDefault();

    // Prevent the form from being submitted if no search value was entered
    if (!this.submitEmptyResults && isNullOrUndefinedOrEmpty(this._searchValue)) {
      evt.stopPropagation();
      return;
    }

    // Dispatch the `searchSubmit` event
    this.searchSubmit.emit({ evt, value: this._searchValue });

    // Invoke the provided callback function, if any
    if (this.searchSubmitHandler && typeof this.searchSubmitHandler === 'function') {
      this.searchSubmitHandler({ evt, value: this._searchValue });
    }
  };

  /**
   * Handles clicking on a suggestion
   * @param {Event} evt The event obj
   * @param {SearchSuggestion} selectedItem The selected suggestion
   * @return {void}
   */
  private onSuggestionClickHandler = (evt: Event, selectedItem: SearchSuggestion): void => {
    if (!isNullOrUndefined(this.suggestionClickHandler)) {
      this.suggestionClickHandler({ evt, item: selectedItem, value: this._searchValue });
    }
    this.suggestionClick.emit({ evt, item: selectedItem, value: this._searchValue });
    if (!isNullOrUndefinedOrEmpty(selectedItem.link)) {
      window.location.assign(selectedItem.link);
    }
  };

  /**
   * Reset the active highlighted suggestion, if there are suggestions
   * @return {void}
   */
  private resetHighlightedSuggestion = (): void => {
    const suggestionsBox = this.getSuggestionsBox();
    if (!isNullOrUndefined(suggestionsBox)) {
      this.getSuggestionsBox().classList.remove(`highlight-${this.activeSuggestionIndex}`);
      if (this.activeSuggestionIndex) {
        this.getSuggestionsBox().children[this.activeSuggestionIndex - 1].classList.remove('is-active');
      }
    }
    this.activeSuggestionIndex = 0;
  };

  /**
   * Move up or down in the suggestions dropdown
   * @param {number} suggestionIndex The index of the selected suggestion
   * @return {void}
   */
  private setActiveSuggestion = (suggestionIndex: number): void => {
    if (!isNaN(suggestionIndex)) {
      const oldActiveSuggestion = this.activeSuggestionIndex;

      // Set new active suggestions
      if (suggestionIndex > this.suggestionsData.length) {
        this.activeSuggestionIndex = 1;
      } else if (suggestionIndex < 1) {
        this.activeSuggestionIndex = this.suggestionsData.length;
      } else {
        this.activeSuggestionIndex = suggestionIndex;
      }

      // Set active suggestion
      this.getSuggestionsBox().children[this.activeSuggestionIndex - 1].classList.add('is-active');
      if (oldActiveSuggestion) {
        this.getSuggestionsBox().children[oldActiveSuggestion - 1].classList.remove('is-active');
      }
      this.getSuggestionsBox().setAttribute('aria-active-suggestion', `active-item-${this.activeSuggestionIndex}`);
    }
  };

  private getSearchBox(): HTMLElement {
    const searchBar = !isNullOrUndefined(this.el.shadowRoot) ? this.el.shadowRoot.querySelector('.c-search__bar') : this.el.children[0].children[0];
    return searchBar.querySelector('.c-search__input');
  }

  private getSuggestionsBox(): HTMLElement {
    const searchBar = !isNullOrUndefined(this.el.shadowRoot) ? this.el.shadowRoot.querySelector('.c-search__bar') : this.el.children[0].children[0];
    return searchBar.querySelector('.c-search__suggestions');
  }

  protected componentWillLoad(): void {
    Pipeline.addComponent(this.el);
    this._searchValue = this.searchValue || this._searchValue;
  }

  protected componentDidLoad(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.el.focus = () => {
      this.getSearchBox().focus();
    };
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    // Icon
    const $searchIcon = !this.hasSearchButton
      ? generateSVGImage({ data: iconSearchPath, height: iconSearchHeight, width: iconSearchWidth }, 'c-search__icon')
      : null;

    // Input
    const searchInputClasses = ['c-search__input'];
    if (this.disabled) {
      searchInputClasses.push('is-disabled');
    }
    if (this._searchValue && this.suggestionsData?.length) {
      searchInputClasses.push('has-suggestions');
    }
    const searchInputAttributes = {
      autocomplete: 'off',
      autofocus: this.autoFocus,
      disabled: this.disabled,
      onClick: this.onInputClickHandler,
      onFocus: this.onFocusHandler,
      onBlur: this.onBlurHandler,
      onKeyUp: this.onKeyUpHandler,
      onInput: this.onInputChangeHandler,
      placeholder: this.placeholder,
      maxlength: this.maxlength,
      name: this.name,
      spellCheck: false,
      type: 'search',
      value: this._searchValue,
      'aria-label': this.inputLabel,
    };
    const $searchInput = (<input class={searchInputClasses.join(' ')} {...searchInputAttributes} />) as HTMLElement;

    // Button
    let $searchButton = null;
    if (this.hasSearchButton) {
      const attributes: {
        disabled: boolean;
        tabIndex: number;
        value: string;
        'aria-label': string;
        class?: string;
      } = {
        disabled: this.disabled,
        tabIndex: 0,
        value: Dictionary.get('Search', 'SEARCH'),
        'aria-label': this.buttonLabel,
      };
      if (this.disabled) {
        attributes['class'] = 'is-disabled';
      }
      $searchButton = (
        <div class={`c-search__button`}>
          <button type="submit" {...attributes} data-track="true" data-track-value={this.buttonLabel}>
            {generateSVGImage({ data: iconSearchPath, height: iconSearchHeight, width: iconSearchWidth })}
          </button>
        </div>
      ) as HTMLElement;
    }

    // Clear
    const $searchClear = !isNullOrUndefinedOrEmpty(this._searchValue)
      ? ((
          <button
            class="c-search__clear"
            type="button"
            data-track="true"
            data-track-value="Clear search"
            onClick={this.onInputClearHandler}
            aria-label="Clear search"
          >
            {generateSVGImage({ data: iconTimesPath, height: iconTimesHeight, width: iconTimesWidth })}
          </button>
        ) as HTMLElement)
      : null;

    // Suggestions
    const $searchSuggestions =
      !isNullOrUndefinedOrEmpty(this._searchValue) && this.suggestionsData?.length
        ? [
            <span class="c-search__suggestions-alert" role="alert">
              {this.suggestionsData.length} results found, use arrow keys to navigate
            </span>,
            <div class="c-search__suggestions">
              {this.suggestionsData.slice(0, this._suggestionsLimit).map((item, index) => {
                return (
                  <span
                    class="c-search__suggestions__suggestion"
                    // Binding the methods in the render is an anti-pattern and might cause performance issues
                    onClick={(evt) => this.onSuggestionClickHandler(evt, item)}
                    onMouseOver={() => this.setActiveSuggestion(index + 1)}
                    tabindex="-1"
                  >
                    <span class="link">{item.name}</span>
                    {!isNullOrUndefinedOrEmpty(item.desc) ? <p class="description">{item.desc}</p> : null}
                  </span>
                ) as VNode;
              })}
            </div>,
          ]
        : null;

    // Form
    const formClasses = ['c-search'];
    if (this.disabled) {
      formClasses.push('is-disabled');
    }
    if (this.hasSearchButton) {
      formClasses.push('has-button');
    }
    if (this.hideClearButton) {
      formClasses.push('hide-clear-button');
    }

    const hostAttributes = {
      'aria-disabled': this.disabled ? 'true' : null,
    };

    return (
      <form class={formClasses.join(' ')} onSubmit={this.onSubmitHandler} role="search">
        <div class="c-search__bar" {...hostAttributes}>
          {$searchIcon}
          {$searchInput}
          {this.hideClearButton ? null : $searchClear}
          {$searchSuggestions}
        </div>
        {$searchButton}
      </form>
    ) as VNode;
  }
}
